'use client';

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { config } from '@ps/config';
import { useAuthToken } from '@ps/functions/auth/useAuthToken';
import { makeDebug } from '@ps/functions/utils/debug';
import { BehaviorSubject } from 'rxjs';
import { type Socket, io } from 'socket.io-client';

import { isChatDebugEnabled } from '../../functions/debug';
import { decodeChatGatewayEventData } from '../../functions/decodeChatGatewayEventData';
import {
  ChatGatewayContext,
  type ChatGatewayContextValue,
  type GatewayConnectionStatus,
} from './context';

const kGatewayUrl = config.chatGatewayWsEndpoint;

const debug = makeDebug('chat:gateway', isChatDebugEnabled('gateway'));
const debugIO = makeDebug('chat:gateway:io', isChatDebugEnabled('gatewayIO'));

// Re-export for backward compatibility with consumers that imported
// the decode helper transitively from the provider module.
export { decodeChatGatewayEventData };

interface Props {
  children?: ReactNode;
}

/**
 * Owns the socket.io connection to the chat gateway. Connects when
 * the Firebase auth token is available, re-emits the token on the
 * socket whenever it refreshes, and reconnects automatically after
 * visibility/online transitions.
 *
 * Web vs legacy: `AppState` (React Native) is replaced by
 * `document.visibilitychange` + `window.online`. The connect /
 * reconnect / disconnect lifecycle, the status$ subject, and the
 * addEventListener registry all mirror the legacy provider.
 */
export function ChatGatewayProvider({ children }: Props) {
  const [status$] = useState(
    () => new BehaviorSubject<GatewayConnectionStatus>('idle'),
  );

  const [socket] = useState<Socket>(() =>
    io(kGatewayUrl, {
      autoConnect: false,
      reconnection: true,
      timeout: 120000,
      transports: ['websocket'],
    }),
  );

  const initialized = useRef(false);
  const [token, refreshToken] = useAuthToken();
  const isRefreshingToken = useRef(false);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const socketRef = useRef(socket);
  socketRef.current = socket;
  const status$Ref = useRef(status$);
  status$Ref.current = status$;

  const handlers = useMemo(() => {
    const connect = () => {
      const t = tokenRef.current;
      if (!t) {
        debug.warn('no user auth token available, connect skipped');
        return;
      }
      status$Ref.current.next('connecting');
      debug.info('connecting to', kGatewayUrl);
      socketRef.current.connect();
    };

    const reconnect = () => {
      if (
        status$Ref.current.value === 'connecting' ||
        isRefreshingToken.current
      ) {
        return;
      }
      isRefreshingToken.current = true;
      debug.info('refreshing user authentication token...');
      refreshToken()
        .then(next => {
          isRefreshingToken.current = false;
          if (!next) {
            debug.warn('user session seems expired');
            return;
          }
          debug.success('auth token refresh successful');
        })
        .catch(e => {
          isRefreshingToken.current = false;
          debug.error('failed to refresh user auth token:', e);
          console.error(e);
        });
    };

    const disconnect = () => {
      debug.info('disconnecting...');
      socketRef.current.disconnect();
      status$Ref.current.next('disconnected');
    };

    const addEventListener = (
      name: string,
      listener: (data: unknown) => void,
      tag = 'x',
    ) => {
      socketRef.current.on(name, listener);
      debug.info('added event listener', name, tag);
      return () => {
        debug.warn('removed event listener', name, tag);
        socketRef.current.off(name, listener);
      };
    };

    const emitSessionToken = () => {
      const t = tokenRef.current;
      if (!t) return;
      socketRef.current.emit('auth.jwt', t);
      debug.success('emitted current user auth token');
    };

    return {
      connect,
      reconnect,
      disconnect,
      addEventListener,
      emitSessionToken,
    };
  }, [refreshToken]);

  useEffect(() => {
    const s = socket;
    const subj = status$;

    s.on('connect', () => {
      debug.success('connected to', kGatewayUrl);
      handlers.emitSessionToken();
      subj.next('connected');
    });
    s.on('disconnect', () => {
      debug.warn('disconnected');
      subj.next('disconnected');
    });
    s.io.on('open', () => debugIO('websocket transport open'));
    s.io.on('close', () => debugIO('websocket transport closed'));
    s.io.on('error', err => {
      debugIO.error('websocket transport error:', err);
      subj.next('error');
    });
    s.io.on('reconnect', attempt => {
      subj.next('connected');
      debugIO('reconnected after attempts:', attempt);
    });
    s.io.on('reconnect_attempt', attempt => {
      if (subj.value !== 'connecting') subj.next('connecting');
      debugIO('reconnecting...', attempt);
    });
    s.io.on('reconnect_error', err => {
      subj.next('error');
      debugIO.error('reconnect error:', err);
    });
    s.io.on('reconnect_failed', () => {
      subj.next('error');
      debugIO.error('failed to reconnect to', kGatewayUrl);
    });

    return () => {
      debug.info('cleanup and dispose');
      s.disconnect();
      s.removeAllListeners();
      s.io.removeAllListeners();
      s.io.engine?.close();
    };
  }, [socket, status$, handlers]);

  useEffect(() => {
    if (!token) return;
    if (!initialized.current) initialized.current = true;
    if (
      (status$.value !== 'connected' && status$.value !== 'connecting') ||
      !socket.connected ||
      socket.disconnected
    ) {
      handlers.connect();
    }
    if (socket.connected) {
      handlers.emitSessionToken();
    }
  }, [token, socket, status$, handlers]);

  // Tear the connection down when the user signs out.
  const prevTokenRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevTokenRef.current;
    if (prev && !token) {
      debug.info('disconnect and cleanup on user signed out');
      handlers.disconnect();
    }
    prevTokenRef.current = token;
  }, [token, handlers]);

  // Web equivalent of the legacy `AppState` foreground/background hook.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      setTimeout(() => {
        if (socket.connected) {
          debug.success('stays connected on page becomes foreground');
        } else {
          debug.warn('connection lost while in background, reconnecting...');
          handlers.reconnect();
        }
      }, 3000);
    };

    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('online', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [socket, handlers]);

  const value = useMemo<ChatGatewayContextValue>(
    () => ({
      status$,
      addEventListener: handlers.addEventListener,
      reconnect: handlers.reconnect,
    }),
    [handlers, status$],
  );

  return (
    <ChatGatewayContext.Provider value={value}>
      {children}
    </ChatGatewayContext.Provider>
  );
}
